import { useRef } from 'react'

import { useTranslation } from 'react-i18next'

import { showToast } from '@/shared/lib/sonner'

import { Label } from '../label'

interface FileUploadProps {
  onFileSelect: (file: File) => void
  selectedFile: File | null
}

const KILOBYTE = 1024
const MEGABYTE = 100
const MAX_FILE_SIZE = MEGABYTE * KILOBYTE * KILOBYTE
const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg']

export function FileUpload({ onFileSelect, selectedFile }: Readonly<FileUploadProps>) {
  const { t } = useTranslation('common')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!ALLOWED_TYPES.includes(file.type)) {
      showToast({ message: t('components.file_upload.errors.extensao_invalida'), variant: 'error' })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast({ message: t('components.file_upload.errors.tamanho_maximo'), variant: 'error' })
      return
    }

    onFileSelect(file)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleClick = () => {
    inputRef.current?.click()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (!file) return

    if (!ALLOWED_TYPES.includes(file.type)) {
      showToast({ message: t('components.file_upload.errors.extensao_invalida'), variant: 'error' })
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast({ message: t('components.file_upload.errors.tamanho_maximo'), variant: 'error' })
      return
    }

    if (file) {
      onFileSelect(file)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  return (
    <div className="w-full space-y-2 text-sm text-gray-700">
      <Label className="font-nunito block cursor-pointer font-medium" onDrop={handleDrop} onDragOver={handleDragOver}>
        <p className="pb-2">{t('components.file_upload.label')}</p>
        <div className="border-input focus-visible:border-ring focus-visible:ring-ring/10 flex h-10 items-center overflow-hidden rounded-md border bg-transparent text-sm shadow-xs transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-not-allowed md:text-sm">
          <button
            type="button"
            onClick={handleClick}
            className="font-nunito h-10 border-r px-4 py-2 font-medium whitespace-nowrap text-gray-700 hover:bg-gray-100"
          >
            {t('components.file_upload.button')}
          </button>
          <span className="font-nunito truncate overflow-hidden px-4 py-2 font-semibold text-ellipsis whitespace-nowrap text-gray-600">
            {selectedFile?.name ?? t('components.file_upload.empty')}
          </span>
          <input
            data-testid="file-upload-input"
            type="file"
            accept=".pdf, .png, .jpg, .jpeg"
            ref={inputRef}
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </Label>
    </div>
  )
}
